import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { feeStructuresService, type FeeStructure } from "@/lib/services";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/lib/utils";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const QS = ["q1", "q2", "q3", "q4"] as const;
type Q = typeof QS[number];

const slug = (c: string) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
type Row = { class_name: string; section: string | null };
const keyOf = (c: string, s?: string | null) => (s ? `${c} ${s}` : c);

export function FeeStructuresPanel() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Row | null>(null);
  const { data, isPending } = useQuery({ queryKey: ["fee-structures"], queryFn: () => feeStructuresService.list() });

  const remove = useMutation({
    mutationFn: (r: Row) => feeStructuresService.remove(r.class_name, r.section),
    onSuccess: (r) => { toast.success(`Fee cleared for ${r.deleted}`); qc.invalidateQueries({ queryKey: ["fee-structures"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const byKey: Record<string, FeeStructure> = Object.fromEntries((data?.structures ?? []).map((s) => [keyOf(s.class_name, s.section), s]));
  const rows: Row[] = data?.rows ?? [];

  if (isPending) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <Card data-testid="fee-structures-panel">
      <CardContent className="p-0">
        <div className="border-b p-4 text-sm text-muted-foreground">
          Set the yearly fee and its four quarter amounts for each grade. Cashiers see these amounts on the Collect Fees screen; grades marked "Not set" use the default amounts.
        </div>
        {!rows.length ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">No classes found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead className="text-right">Annual</TableHead>
                {QS.map((q) => <TableHead key={q} className="text-right">{q.toUpperCase()}</TableHead>)}
                <TableHead>Status</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const c = keyOf(r.class_name, r.section);
                const s = byKey[c];
                return (
                  <TableRow key={c} data-testid={`fee-structure-row-${slug(c)}`}>
                    <TableCell className="font-semibold">{c}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold" data-testid={`fee-structure-annual-${slug(c)}`}>{s ? formatINR(s.annual_amount) : "—"}</TableCell>
                    {QS.map((q) => <TableCell key={q} className="text-right tabular-nums">{s ? formatINR(s[q]) : "—"}</TableCell>)}
                    <TableCell>
                      {s ? <Badge variant={"success" as any} data-testid={`fee-structure-status-${slug(c)}`}>Set</Badge>
                        : <Badge variant="secondary" data-testid={`fee-structure-status-${slug(c)}`}>Not set</Badge>}
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`fee-structure-edit-${slug(c)}`}><Pencil className="h-4 w-4" /></Button>
                      {s && (
                        <Button size="sm" variant="ghost" disabled={remove.isPending}
                          onClick={() => { if (window.confirm(`Clear the fee for ${c}? It will go back to the default amounts.`)) remove.mutate(r); }}
                          data-testid={`fee-structure-delete-${slug(c)}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {editing && <FeeStructureDialog row={editing} existing={byKey[keyOf(editing.class_name, editing.section)]} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function FeeStructureDialog({ row, existing, onClose }: { row: Row; existing?: FeeStructure; onClose: () => void }) {
  const className = keyOf(row.class_name, row.section);
  const qc = useQueryClient();
  const init = (v?: number) => (v === undefined ? "" : String(v));
  const [annual, setAnnual] = useState(init(existing?.annual_amount));
  const [qs, setQs] = useState<Record<Q, string>>({ q1: init(existing?.q1), q2: init(existing?.q2), q3: init(existing?.q3), q4: init(existing?.q4) });

  const num = (v: string) => (v === "" ? NaN : Number(v));
  const annualN = num(annual);
  const qN = QS.map((q) => num(qs[q]));
  const allFilled = !isNaN(annualN) && qN.every((v) => !isNaN(v));
  const anyNegative = annualN < 0 || qN.some((v) => v < 0);
  const sum = qN.reduce((a, v) => a + (isNaN(v) ? 0 : v), 0);
  const matches = allFilled && Math.abs(sum - annualN) <= 0.01;
  const diff = (isNaN(annualN) ? 0 : annualN) - sum;

  const save = useMutation({
    mutationFn: () => feeStructuresService.upsert(row.class_name, row.section, { annual_amount: annualN, q1: qN[0], q2: qN[1], q3: qN[2], q4: qN[3] }),
    onSuccess: () => { toast.success(`Fee saved for ${className}`); qc.invalidateQueries({ queryKey: ["fee-structures"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="fee-structure-dialog">
        <DialogHeader>
          <DialogTitle>Fee for {className}</DialogTitle>
          <DialogDescription>Enter the yearly fee and each quarter's amount. The four quarters must add up to the yearly fee.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Annual fee (₹)</Label>
            <Input type="number" min={0} value={annual} onChange={(e) => setAnnual(e.target.value)} data-testid="fee-structure-annual-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            {QS.map((q) => (
              <div key={q}><Label>{q.toUpperCase()} (₹)</Label>
                <Input type="number" min={0} value={qs[q]} onChange={(e) => setQs({ ...qs, [q]: e.target.value })} data-testid={`fee-structure-${q}-input`} /></div>
            ))}
          </div>
          <div className={`rounded-md border p-3 text-sm ${matches ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="fee-structure-sum-status">
            Quarters total {formatINR(sum)}
            {matches ? " — matches the yearly fee." : !allFilled ? " — fill in all amounts." : ` — ${formatINR(Math.abs(diff))} ${diff > 0 ? "short of" : "over"} the yearly fee.`}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fee-structure-cancel-btn">Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!matches || anyNegative || save.isPending} data-testid="fee-structure-save-btn">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
